// src/hr/kit/Ring.js -- SVG progress ring that draws itself in on load; center slot for a label.
import React, { useId } from 'react';
import { cx, plainId } from './util';

// value / max -> share of the circle (clamped to 0..1). Without `color` the stroke is the accent
// gradient; pass a color for the compact type rings (Sick, Personal ...).
export function Ring({
  value = 0, max = 100, size = 118, stroke = 10, color, label, sub, ariaLabel, children, className
}) {
  const gid = `hrp-ring-${plainId(useId())}`;
  const center = size / 2;
  // Inset a little more than half the stroke so the round cap and glow are never clipped.
  const r = Math.max(1, (size - stroke) / 2 - Math.max(1, stroke * 0.4));
  const circumference = 2 * Math.PI * r;
  const share = Number(max) > 0 ? Math.min(1, Math.max(0, (Number(value) || 0) / Number(max))) : 0;
  const offset = circumference * (1 - share);
  const name = ariaLabel || [label, sub].filter((x) => x != null && x !== '').join(' ') || `${Math.round(share * 100)}%`;

  return (
    <div
      className={cx('hrp-ring', !color && 'hrp-ring--grad', className)}
      style={{ width: size, height: size, '--hrp-ring-s': `${size}px` }}
      role="img"
      aria-label={name}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
        {!color && (
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#a5b4fc" />
              <stop offset="0.55" stopColor="#667eea" />
              <stop offset="1" stopColor="#9f7aea" />
            </linearGradient>
          </defs>
        )}
        <circle className="hrp-ring__track" cx={center} cy={center} r={r} fill="none" strokeWidth={stroke} />
        <circle
          className="hrp-ring__val"
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke={color || `url(#${gid})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          opacity={share > 0 ? undefined : 0}
          transform={`rotate(-90 ${center} ${center})`}
          style={{ '--hrp-len': circumference }}
        />
      </svg>
      <div className="hrp-ring__ctr" aria-hidden="true">
        {children != null ? children : (
          <>
            {label != null && <b>{label}</b>}
            {sub && <span>{sub}</span>}
          </>
        )}
      </div>
    </div>
  );
}

export default Ring;
